import axiosInstance from "@/utils/axiosInstance";
import { TRANSFER_SERVICES } from "@/utils/transferServices";
import { wrapFetchValue } from "@/store/modules/storeCache";
import store from "@/store";

const baseUrl = `api/keys`;

const state = {};

const getters = {};

const mutations = {};

const actions = {
  getById({commit}, payload: { id: number, options: object }) {
    return axiosInstance.get(`${baseUrl}/${payload.id}`, {params: payload.options})
      .then((data) => data.data);
  },
  getSubKeys({commit}, payload: { id: number, params?: object}) {
    return axiosInstance.get(`${baseUrl}/${payload.id}/sub-keys`, {params: payload?.params})
      .then((data) => data.data);
  },
  getKeyManagersSoloById({commit}, payload: { id: number, pagination: any }) {
    return axiosInstance.get(`${baseUrl}/${payload.id}/managers`, {
      params: payload.pagination,
    }).then((data) => data.data);
  },
  getKeyManagersGroupsById({commit}, payload: { id: number, pagination: any }) {
    return axiosInstance.get(`${baseUrl}/${payload.id}/managers-groups`, {
      params: payload.pagination,
    }).then((data) => data.data);
  },
  getGuestsSoloById({commit}, payload: { id: number, pagination: any }) {
    return axiosInstance.get(`${baseUrl}/${payload.id}/guests`, {
      params: payload.pagination,
    }).then((data) => data.data);
  },
  getGuestsGroupsById({commit}, payload: { id: number, pagination: any }) {
    return axiosInstance.get(`${baseUrl}/${payload.id}/guests-groups`, {
      params: payload.pagination,
    }).then((data) => data.data);
  },
  getKeyAccessById({commit}, payload: { id: number, params?: object }) {
    return axiosInstance.get(`${baseUrl}/${payload.id}/key-access`)
      .then((data) => data.data);
  },
  create({commit}, payload: { key: object }) {
    return axiosInstance.post(baseUrl, {key: payload.key}).then((data) => data.data);
  },
  createSubKeys({commit}, payload: { keyId: number, subKeys: any[] }) {
    return axiosInstance.post(`${baseUrl}/${payload.keyId}/sub-keys`, {subKeys: payload.subKeys})
      .then((data) => data.data);
  }, getStatus({ commit }) {
    return wrapFetchValue(state, "status", () => axiosInstance.get(`${baseUrl}/status`)
      .then((data) => data.data));
  },
  getKeyUsersById({commit}, payload: {
    id: number
    search: string | null,
    page: number,
    itemsPerPage: number,
  }) {
    return axiosInstance.get(`${baseUrl}/${payload.id}/users`, { params: {
      search: payload.search,
      page: payload.page,
      itemsPerPage: payload.itemsPerPage,
    }}).then((data) => data.data);
  },
  addManagers({commit}, payload: { id: any, userGroups: any[] }) {
    return axiosInstance.post(`${baseUrl}/${payload.id}/managers`, {
      userGroups: payload.userGroups,
    }).then((data) => data.data);
  },
  addGuests({commit}, payload: {
    id: number,
    userGroups: any[],
    userGroupsIds: number[],
    userGroupsData: any[],
    users: any[],
    invitation: object,
  }) {
    return axiosInstance.post(`${baseUrl}/${payload.id}/guests`, {
      userGroups: payload.userGroups,
      userGroupsIds: payload.userGroupsIds,
      userGroupsData: payload.userGroupsData,
      users: payload.users,
      invitation: payload.invitation,
    }).then((data) => data.data);
  },
  drop({commit}, payload: {
    keyId: number,
    token: number,
    referentId: number,
    relayId: number,
    holderId: number,
    keyToGuest: number,
    notificationType: string,
  }) {
    return axiosInstance.put(`${baseUrl}/${payload.keyId}/drop`, {
      transferService: TRANSFER_SERVICES.MYLOBY,
      token: payload.token,
      referentId: payload.referentId,
      relayId: payload.relayId,
      holderId: payload.holderId,
      keyToGuest: payload.keyToGuest,
      notificationType: payload.notificationType,
    }).
    then((data) => data.data);
  },
  withdraw({commit}, payload: {
    keyId: number,
    token: number,
    referentId: number,
    estimatedEndDate: string,
    keyToGuest?: number,
    notificationType?: string,
  }) {
    return axiosInstance.put(`${baseUrl}/${payload.keyId}/withdraw`, {
      transferService: TRANSFER_SERVICES.MYLOBY,
      token: payload.token,
      referentId: payload.referentId,
      estimatedEndDate: payload.estimatedEndDate,
      keyToGuest: payload.keyToGuest,
      notificationType: payload.notificationType,
    }).
    then((data) => data.data);
  },
  extendKeyHolding({commit}, payload: { keyId: number, newEstimatedEndDate: string }) {
    return axiosInstance.put(`${baseUrl}/${payload.keyId}/extend-key-holding`, {
      newEstimatedEndDate: payload.newEstimatedEndDate,
    }).
    then((data) => data.data);
  },
  transit({commit}, payload: { keyId: number, sourceUserId: number, targetRelayId: number }) {
    return axiosInstance.put(`${baseUrl}/${payload.keyId}/transit`, payload).
    then((data) => data.data);
  },
  retrieve({commit}, payload: { keyId: number, userId: number }) {
    return axiosInstance.put(`${baseUrl}/${payload.keyId}/retrieve`, payload).
    then((data) => data.data);
  },
  retrieveFromTransit({commit}, payload: { keyId: number, relayId: number }) {
    return axiosInstance.put(`${baseUrl}/${payload.keyId}/retrieve-transit`, {
      relayId: payload.relayId,
    }).
    then((data) => data.data);
  },
  reportMissing({commit}, payload: { keyId: number, relayId: number }) {
    return axiosInstance.put(`${baseUrl}/${payload.keyId}/report-missing`, {
      relayId: payload.relayId,
    }).
    then((data) => data.data);
  },
  updateById({commit}, payload: { id: number, data: any }) {
    return axiosInstance.put(`${baseUrl}/${payload.id}`, payload.data)
      .then((data) => data.data);
  },
  reserveLocation({commit}, payload: { keyId: number , locationId: number}) {
    return axiosInstance.put(`${baseUrl}/${payload.keyId}/reserved-location`, {
      locationId: payload.locationId,
    }).then((data) => data.data);
  },
  editRelays({commit}, payload: { keyId: number, relayIds: number[]}) {
    return axiosInstance.put(`${baseUrl}/${payload.keyId}/relays`, {
      relayIds: payload.relayIds,
    }).then((data) => data.data);
  },
  import({commit}, payload: {keys: object}) {
    return axiosInstance.post(`${baseUrl}/import`, {
      keys: payload.keys,
      companyId: store.getters.envCompanyId,
    }).then((data) => data.data);
  },
  deleteById({commit}, payload: { id: number }) {
    return axiosInstance.delete(`${baseUrl}/${payload.id}`).then((data) => data.data);
  },
  sendSms({commit}, payload: {
    keyId: number,
    sourceUserId: number,
    targetUserId: number,
    acceptOverPrice: boolean,
    fromModal: boolean,
  }) {
    return axiosInstance.post(`${baseUrl}/${payload.keyId}/send-sms`, {
        sourceUserId: payload.sourceUserId,
        targetUserId : payload.targetUserId,
        acceptOverPrice: payload.acceptOverPrice,
        fromModal: payload.fromModal,
    })
    .then((data) => data.data);
  },
  sendMail({commit}, payload: {
    keyId: number,
    sourceUserId: number,
    targetUserId: number,
    acceptOverPrice: boolean,
  }) {
    return axiosInstance.post(`${baseUrl}/${payload.keyId}/send-mail`, {
      sourceUserId: payload.sourceUserId,
      targetUserId : payload.targetUserId,
      acceptOverPrice: payload.acceptOverPrice,
    })
      .then((data) => data.data);
  },
  pinVerification({commit}, payload: {
    keyId: number,
    pin: string,
    targetUserId: number,
    acceptOverPrice: boolean,
    notificationType: string,
  }) {
    return axiosInstance.post(`${baseUrl}/${payload.keyId}/pin-verification`,
      {
        pin: payload.pin,
        targetUserId : payload.targetUserId,
        acceptOverPrice: payload.acceptOverPrice,
        notificationType: payload.notificationType,
      })
    .then((data) => data.data);
  },
  //#region HandToHand
  giveHandToHand({commit}, payload: { keyId: number, sourceUserId: number, targetUserId: number }) {
    return axiosInstance.post(`${baseUrl}/${payload.keyId}/give-hand-to-hand`,
      {sourceUserId: payload.sourceUserId, targetUserId : payload.targetUserId})
    .then((data) => data.data);
  },
  checkHandToHand({commit}, payload: { keyId: number, targetUserId: number }) {
    return axiosInstance.get(`${baseUrl}/${payload.keyId}/check-hand-to-hand/${payload.targetUserId}`)
    .then((data) => data.data);
  },
  cancelHandToHand({commit}, payload: { keyId: number, sourceUserId: number, targetUserId: number }) {
    return axiosInstance.post(`${baseUrl}/${payload.keyId}/cancel-hand-to-hand`,
      {})
    .then((data) => data.data);
  },
  recoverHandToHand({commit}, payload: { keyId: number, token: string}) {
    return axiosInstance.post(`${baseUrl}/${payload.keyId}/recover-hand-to-hand`,
      {token: payload.token})
    .then((data) => data.data);
  },
  //#endregion HandToHand
  archive({commit}, payload: { keyId: number}) {
    return axiosInstance.post(`${baseUrl}/${payload.keyId}/archive`)
      .then((data) => data.data);
  },
  invitationReminder({commit}, payload: {
    keyId: number,
    guestId: number,
    notificationType: string,
    acceptOverPrice: boolean,
  }) {
    return axiosInstance.post(`${baseUrl}/${payload.keyId}/invitation-reminder`,
      {
        guestId: payload.guestId,
        notificationType: payload.notificationType,
        acceptOverPrice: payload.acceptOverPrice,
      })
      .then((data) => data.data);
  },
  giveKeyToGuest({commit}, payload: {
    keyId: number,
    sourceRelayId: number,
    targetUserId: number,
    notificationType: string,
    acceptOverPrice: boolean,
    fromModal: boolean,
  }) {
    return axiosInstance.post(`${baseUrl}/${payload.keyId}/give-key-to-guest`, {
      sourceRelayId: payload.sourceRelayId,
      targetUserId : payload.targetUserId,
      notificationType : payload.notificationType,
      acceptOverPrice : payload.acceptOverPrice,
      fromModal : payload.fromModal,
    }).then((data) => data.data);
  },
  //#region Connected boxes
  dropOnConnected({commit}, payload: {
    keyId: number,
    transferService: string,
    askNewCode: boolean,
    referentId: number, // @TODO: Remove ?
    // Specific to drop
    holderId?: number,
    connectedBoxId?: string,
    sizeId?: string,
  }) {
    return axiosInstance.put(`${baseUrl}/${payload.keyId}/drop`, {
      transferService: payload.transferService,
      askNewCode: payload.askNewCode,
      referentId: payload.referentId,
      holderId: payload.holderId,
      connectedBoxId: payload.connectedBoxId,
      sizeId: payload.sizeId,
    }).then((data) => data.data);
  },
  withdrawFromConnected({commit}, payload: {
    keyId: number,
    transferService: string,
    askNewCode: boolean,
    referentId: number, // @TODO: Remove ?
    // Specific to withdraw
    estimatedEndDate: number,
  }) {
    return axiosInstance.put(`${baseUrl}/${payload.keyId}/withdraw`, {
      transferService: payload.transferService,
      askNewCode: payload.askNewCode,
      referentId: payload.referentId,
      estimatedEndDate: payload.estimatedEndDate,
    }).then((data) => data.data);
  },
  getConnectedKeyMove({commit}, payload: { id: number, connectedCodeId: number }) {
    return axiosInstance.get(`${baseUrl}/${payload.id}/connected-key-move`, {
      params: { connectedCodeId: payload.connectedCodeId },
    }).then((data) => data?.data);
  },
  lockerNotOpen({commit}, payload: {
    keyId: number,
    connectedCodeId: number,
  }) {
    return axiosInstance.post(`${baseUrl}/${payload.keyId}/locker-not-open`,
      {
        connectedCodeId: payload.connectedCodeId,
      }).then((data) => data.data);
  },
  keyIsNotInside({commit}, payload: {
    keyId: number,
    connectedCodeId: number,
  }) {
    return axiosInstance.post(`${baseUrl}/${payload.keyId}/not-inside`,
      {
        connectedCodeId: payload.connectedCodeId,
      })
      .then((data) => data.data);
  },
  //#endregion Connected boxes
  //#region Pictures
  getAllPictures({commit}, payload: {id: number}) {
    return axiosInstance.get(`${baseUrl}/${payload.id}/pictures`)
      .then((data) => data.data);
  },
  addPicture({commit}, payload: {keyId: number, picture: any}) {
    return axiosInstance.post(`${baseUrl}/${payload.keyId}/pictures`, payload.picture)
      .then((data) => data.data);
  },
  deletePicture({commit}, payload: {keyId: number, keyPictureId: number}) {
    return axiosInstance.delete(`${baseUrl}/${payload.keyId}/pictures/${payload.keyPictureId}`)
    .then((data) => data.data);
  },
  updatePicture({commit}, payload: {keyId: number, keyPictureId: number, keyPictureData: any}) {
    return axiosInstance.put(`${baseUrl}/${payload.keyId}/pictures/${payload.keyPictureId}`, {
      keyPictureData: payload.keyPictureData,
    }).then((data) => data.data);
  },
  updateProfilePicture({commit}, payload: {keyId: number, keyPictureId: number}) {
    return axiosInstance.put(`${baseUrl}/${payload.keyId}/pictures/${payload.keyPictureId}/profile`)
      .then((data) => data.data);
  },
  //#endregion Pictures
  getKeyStatus({commit}, payload: { id: number }) {
    return axiosInstance.get(`${baseUrl}/${payload.id}/status`)
      .then((data) => data.data);
  },
  canDoActions({ commit }, { id, actions }: { id: number, actions: any[] }) {
    return axiosInstance.post(`${baseUrl}/${id}/can-do-actions`, { actions })
      .then((data) => data.data);
  },
  getIsFollowedKey({ commit }, payload: { keyId: number, userId: number }) {
    return axiosInstance.get(`${baseUrl}/${payload.keyId}/users/${payload.userId}/followed`)
      .then((data) => data.data);
  },
  addFollowedKey({ commit }, payload: { keyId: number, userId: number }) {
    return axiosInstance.post(`${baseUrl}/${payload.keyId}/users/${payload.userId}/followed`)
      .then((data) => data.data);
  },
  removeFollowedKey({ commit }, payload: { keyId: number, userId: number}) {
    return axiosInstance.delete(`${baseUrl}/${payload.keyId}/users/${payload.userId}/followed`)
      .then((data) => data.data);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
